import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon } from 'lucide-react';
import { Dialog } from '../portal/Dialog';
import { Button } from '../ui/Button';
import { useAcademy } from '../../context/Academy';
import { useAcademyHub } from '../../context/AcademyHub';
import { INTERESTS } from '../../data/academyCommunity';
import { COURSES } from '../../data/academyCourses';
import { PHOTOS, photoFilter } from '../../data/photos';
import { ensureSharpenFilter } from '../ui/Photo';
import type { InterestId } from '../../types/academyHub';
import { recommendCourses } from '../../utils/academyRecommend';

/** Shown once, straight after a learner creates an account. */
export function OnboardingDialog() {
  const {
    state
  } = useAcademy();
  const {
    hub,
    setInterests,
    finishOnboarding
  } = useAcademyHub();
  const navigate = useNavigate();
  const [step, setStep] = React.useState<'interests' | 'picks'>('interests');
  const [picked, setPicked] = React.useState<InterestId[]>(hub.interests);
  const open = Boolean(state.learner) && !hub.onboarded;
  const toggle = (id: InterestId) => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const recommended = recommendCourses(picked, state.enrolments, 3);
  const close = () => {
    setInterests(picked);
    finishOnboarding();
  };
  return <Dialog open={open} onClose={close} title={step === 'interests' ? `Welcome, ${state.learner?.name.split(' ')[0] ?? ''}` : 'Three courses to start with'} description={step === 'interests' ? undefined : 'Picked from what you told us. They will stay on your dashboard.'} data-id="element-482" data-mp="a1x8e7t4htpd">
      {step === 'interests' ? <div data-id="element-483" data-mp="3ih74mzac04p8">
          <img src={PHOTOS.workshopWide.src} alt={PHOTOS.workshopWide.alt} className="mb-5 aspect-[16/7] w-full rounded-sm object-cover" style={{
        filter: photoFilter(PHOTOS.workshopWide)
      }} ref={() => ensureSharpenFilter()} data-id="element-484" data-mp="3n9rwhv9og7q4" />
          <p className="text-[15px] text-body" data-id="element-485" data-mp="2msrn7ncygbp7">
            Cavalla Academy teaches the methods behind our advisory work, for free. Tell us what you want to get better at and we
            will suggest where to begin.
          </p>
          <fieldset className="mt-6" data-id="element-486" data-mp="2wiui5t3mzzq6">
            <legend className="text-[14px] font-medium text-ink" data-id="element-487" data-mp="2wht6bmesr97e">Areas of interest</legend>
            <div className="mt-3 grid gap-2 sm:grid-cols-2" data-id="element-488" data-mp="2rk9q4gyjgmct">
              {INTERESTS.map(interest => {
            const on = picked.includes(interest.id);
            return <label key={interest.id} className={`flex cursor-pointer items-center gap-3 rounded-sm border px-3.5 py-3 text-[14px] transition-colors duration-150 ${on ? 'border-gold bg-charcoal text-ink' : 'border-line text-body hover:border-gold/50'}`} data-id="element-489" data-mp="1ywdjajf99e2b">
                    <input type="checkbox" checked={on} onChange={() => toggle(interest.id)} className="sr-only" data-id="element-490" data-mp="3k8otu7c7zi8u" />
                    <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border ${on ? 'border-gold bg-gold text-charcoal' : 'border-muted'}`} aria-hidden="true" data-id="element-491" data-mp="3rqmvjpo5tp1i">
                      {on && <CheckIcon className="h-3 w-3" data-id="element-492" data-mp="3msbn9jdijmle" />}
                    </span>
                    {interest.label}
                  </label>;
          })}
            </div>
          </fieldset>
          <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end" data-id="element-493" data-mp="jce9l0g57wib">
            <Button type="button" variant="secondary" onClick={close} data-id="element-494" data-mp="5kpyn0hmre2x">
              Skip for now
            </Button>
            <Button type="button" onClick={() => setStep('picks')} disabled={!picked.length} data-id="element-495" data-mp="9l9v01udqdo0">
              Show my courses
            </Button>
          </div>
        </div> : <div data-id="element-496" data-mp="122w151p8om23">
          <ol className="divide-y divide-line border-y border-line" data-id="element-497" data-mp="122m79312pur4">
            {recommended.map(slug => {
          const course = COURSES.find(c => c.slug === slug);
          if (!course) return null;
          return <li key={slug} className="py-4" data-id="element-498" data-mp="35mf1u67u398e">
                  <p className="text-[15px] font-medium text-ink" data-id="element-499" data-mp="30khb6qx3tc5f">{course.title}</p>
                  <p className="mt-1 text-small text-muted" data-id="element-500" data-mp="2wjxf7qo9oekv">
                    {course.difficulty} · {course.hours} hours · {course.modules.length} modules
                  </p>
                </li>;
        })}
          </ol>
          <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end" data-id="element-501" data-mp="3oc4xswnc211w">
            <Button type="button" variant="secondary" onClick={() => setStep('interests')} data-id="element-502" data-mp="3jj21igr68di0">
              Back
            </Button>
            <Button type="button" onClick={() => {
          close();
          navigate('/academy/dashboard');
        }} data-id="element-503" data-mp="3fgffiwh6lmsq">
              Go to my dashboard
            </Button>
          </div>
        </div>}
    </Dialog>;
}